import React, { useEffect, useRef, useState } from 'react'
import { useHistory, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Switch } from '@headlessui/react'
import useQuery from '@/hooks/useQuery'
import { debounce } from 'throttle-debounce'
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationIcon, XCircleIcon } from '@heroicons/react/solid'
import { useGlobalState } from '@/state'
import { randomToastSuccess } from '@/shared/Toast'
import Tippy from '@tippyjs/react'
import ToolTip from '@/shared/ToolTip'

import Card from '@/shared/Card'
import PrimaryButton from '@/shared/Buttons/Primary'
import OmniAuthSettings from '@/pages/Settings/OmniAuthSettings'
import DeleteAccount from '@/pages/Settings/DeleteAccount'

const PrefSwitch = ({ checked, onChange, label }) => (
  <Switch
    checked={!!checked}
    onChange={onChange}
    aria-label={label}
    className={`${checked ? 'bg-clcpurple' : 'bg-slate-300 dark:bg-slate-600'} relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-clcpurple focus:ring-offset-2`}
  >
    <span
      aria-hidden='true'
      className={`${checked ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
    />
  </Switch>
)

// One notification kind row, the in-app + email toggles + label.
// `dimEmail` greys the email toggle when its parent stream is unsubscribed
// (the toggle still records the user's preference but has no immediate effect).
const KindRow = ({ k, onToggle, dimEmail }) => {
  const inAppAvailable = k.inAppAvailable !== false
  const emailAvailable = k.emailAvailable !== false
  return (
    <li className='py-3'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:gap-4'>
        <div className={`flex-1 text-sm mb-2 sm:mb-0 ${dimEmail ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'}`}>
          {k.label}
        </div>
        <div className='flex items-center gap-6 sm:gap-0 sm:w-32 sm:justify-around'>
          {inAppAvailable ? (
            <label className='flex items-center gap-2 cursor-pointer select-none sm:gap-0'>
              <PrefSwitch checked={k.inApp} onChange={(v) => onToggle(k.kind, 'in_app', v)} label={`${k.label} (in-app)`} />
              <span className='text-xs text-gray-500 dark:text-gray-400 sm:hidden'>In-app</span>
            </label>
          ) : (
            <span className='inline-flex items-center justify-center w-11 h-6 text-xs text-gray-300 dark:text-gray-600 select-none' aria-hidden='true'>-</span>
          )}
          {emailAvailable ? (
            <label className={`flex items-center gap-2 select-none sm:gap-0 ${dimEmail ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
              <PrefSwitch checked={k.email} onChange={(v) => onToggle(k.kind, 'email', v)} label={`${k.label} (email)`} />
              <span className='text-xs text-gray-500 dark:text-gray-400 sm:hidden'>Email</span>
            </label>
          ) : (
            <span className='inline-flex items-center justify-center w-11 h-6 text-xs text-gray-300 dark:text-gray-600 select-none' aria-hidden='true'>-</span>
          )}
        </div>
      </div>
    </li>
  )
}

// Stream card, header with subscribed/unsubscribed status + the kinds inside it.
const StreamCard = ({ stream, kinds, onToggleKind, onToggleStream, busy }) => {
  const isUnsubscribed = !!stream.unsubscribed
  const isPermanent    = !!stream.permanent
  const switchDisabled = isPermanent || busy

  let StatusIcon = CheckCircleIcon
  let statusColor = 'text-green-500'
  let statusText  = 'Subscribed'
  let statusTextColor = 'text-green-600 dark:text-green-400'
  if (isUnsubscribed) {
    if (isPermanent) {
      StatusIcon = ExclamationIcon
      statusColor = 'text-red-500'
      statusText  = `Blocked (${stream.reason})`
      statusTextColor = 'text-red-600 dark:text-red-400'
    } else {
      StatusIcon = XCircleIcon
      statusColor = 'text-gray-400'
      statusText  = 'Unsubscribed'
      statusTextColor = 'text-gray-500 dark:text-gray-400'
    }
  }

  return (
    <div className='p-4 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'>
      <div className='flex items-start gap-3'>
        <StatusIcon className={`h-6 w-6 mt-0.5 flex-shrink-0 ${statusColor}`} />
        <div className='flex-1 min-w-0'>
          <div className='flex flex-wrap items-baseline gap-x-2'>
            <h4 className='text-base font-semibold text-gray-900 dark:text-gray-100'>{stream.label}</h4>
            <span className={`text-xs font-medium ${statusTextColor}`}>{statusText}</span>
          </div>
          <p className='text-sm text-gray-600 dark:text-gray-300 mt-0.5'>{stream.description}</p>
          {isPermanent && (
            <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
              This is a permanent block (spam complaint). Contact support if you believe this is an error.
            </p>
          )}
        </div>
        <div className='flex-shrink-0'>
          <PrefSwitch
            checked={!isUnsubscribed}
            onChange={() => { if (!switchDisabled) { onToggleStream(stream.key, isUnsubscribed) } }}
            label={isUnsubscribed ? `Resubscribe to ${stream.label}` : `Unsubscribe from ${stream.label}`}
          />
          {busy && <div className='text-[10px] text-gray-400 mt-1 text-right'>Saving…</div>}
        </div>
      </div>

      {kinds.length > 0 && (
        <div className='mt-3 pl-9 border-t border-gray-100 dark:border-gray-700 pt-2'>
          {isUnsubscribed && (
            <p className='text-xs italic text-gray-500 dark:text-gray-400 mb-1'>
              Stream is unsubscribed. Email toggles below are overridden until you resubscribe.
            </p>
          )}
          <ul className='divide-y divide-gray-100 dark:divide-gray-700'>
            {kinds.map(k => (
              <KindRow key={k.kind} k={k} onToggle={onToggleKind} dimEmail={isUnsubscribed} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const SettingsNotifications = () => {
  const { getRequest, putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const [kinds, setKinds]     = useState([])
  const [streams, setStreams] = useState([])
  const [loaded, setLoaded]   = useState(false)
  const [streamBusyKey, setStreamBusyKey] = useState(null)

  useEffect(() => {
    getRequest('/api/v1/notification_preferences', {}, (err, data) => {
      setLoaded(true)
      if (err) return
      setKinds(data.kinds || [])
      setStreams(data.streams || [])
    })
  }, [])

  const toggleKind = (kind, channel, value) => {
    const before = kinds
    const next = kinds.map(k => k.kind === kind ? { ...k, [channel === 'in_app' ? 'inApp' : 'email']: value } : k)
    setKinds(next)
    putpostRequest('/api/v1/notification_preferences', 'PATCH', {
      preferences: { [channel]: { [kind]: value } }
    }, (err) => {
      if (err) { setKinds(before); return }
      setToast(randomToastSuccess())
    })
  }

  const toggleStream = (streamKey, currentlyUnsubscribed) => {
    setStreamBusyKey(streamKey)
    const action = currentlyUnsubscribed ? 'subscribe' : 'unsubscribe'
    putpostRequest(`/api/v1/notification_preferences/streams/${streamKey}/${action}`, 'POST', {}, (err, data) => {
      setStreamBusyKey(null)
      if (err) { return }
      setStreams(data.streams || [])
      setKinds(data.kinds || kinds)
      setToast(randomToastSuccess())
    })
  }

  // Group kinds by stream. Kinds with no stream go into `orphans` (in-app-only kinds).
  const kindsByStream = {}
  const orphans = []
  kinds.forEach(k => {
    if (k.stream) {
      kindsByStream[k.stream] = kindsByStream[k.stream] || []
      kindsByStream[k.stream].push(k)
    } else {
      orphans.push(k)
    }
  })

  return (
    <div className='mt-10'>
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Notifications</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Pick what reaches you and how. <em>In-app</em> shows up in the bell at the top of every page; <em>email</em> goes to your inbox.
              Each section is one Postmark stream. Unsubscribing from a stream overrides every email toggle inside it.
            </p>
          </div>
        </div>
        <Card>
          {!loaded ? (
            <div className='py-6 text-sm text-gray-500'>Loading…</div>
          ) : (kinds.length === 0 && streams.length === 0) ? (
            <div className='py-6 text-sm text-gray-500'>No notification kinds configured.</div>
          ) : (
            <div className='space-y-4'>
              {/* Desktop column headers (apply to every list below) */}
              <div className="hidden sm:flex items-center gap-4 pr-1 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <div className="flex-1">Notification</div>
                <div className="w-16 text-center">In-app</div>
                <div className="w-16 text-center">Email</div>
              </div>

              {streams.map(s => (
                <StreamCard
                  key={s.key}
                  stream={s}
                  kinds={kindsByStream[s.key] || []}
                  onToggleKind={toggleKind}
                  onToggleStream={toggleStream}
                  busy={streamBusyKey === s.key}
                />
              ))}

              {orphans.length > 0 && (
                <div className='p-4 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'>
                  <h4 className='text-base font-semibold text-gray-900 dark:text-gray-100'>Other</h4>
                  <p className='text-sm text-gray-600 dark:text-gray-300 mt-0.5'>
                    In-app only, no email channel.
                  </p>
                  <ul className='mt-2 divide-y divide-gray-100 dark:divide-gray-700'>
                    {orphans.map(k => (
                      <KindRow key={k.kind} k={k} onToggle={toggleKind} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

const SettingsGeneral = () => {
  const [user, setUser] = useGlobalState('settingsContainerUser')
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')

  const [, setToast] = useGlobalState('toast')
  let history = useHistory()
  const [timezones, setTimezones] = useState([])
  const { putpostRequest, getRequest } = useQuery()
  const { formState, reset, register, unregister, handleSubmit, setValue, getValues, setError } = useForm({
   defaultValues: {
      firstName: user.firstName,
      timezone: user.timezone,
      email: user.email,
      lastName: user.lastName
    }
  })

  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getRequest('/api/v1/static/timezones', {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }

      if (jsonData.timezones) {
        setTimezones(jsonData.timezones)
        setValue('timezone', user.timezone, { shouldDirty: false, shouldValidate: false })
      }
    })
  }, [])

  const onSubmit = debounce(300, data => {
    setLoading(true)

    putpostRequest(`/api/v1/users/${user.id}`, 'PATCH', { user: data }, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        Object.entries(err).forEach(([key, value]) => {
          setError(key, value)
        })
        return
      }

      setCurrentUser({
        ...currentUser,
        displayName: jsonData.user.displayName,
        timezone: getValues().timezone,
        email: getValues().email,
        firstName: getValues().first_name
      })

      setToast(randomToastSuccess())
    })
  })


  return (
    <div className='mt-10'>
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">General</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              The basics of the basics.
            </p>
          </div>
        </div>
        <Card footer={<PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} disabled={!isDirty} text="Save" />}>
          <form>
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-3 sm:col-span-1">
                <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                  <span>First Name</span>

                  <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <div className="mt-1 flex flex-col rounded-md shadow-sm">
                  <div className='relative'>
                    <input type="text" className={errors.first_name ? 'errors' : ''} {...register('first_name', { required: true }) } defaultValue={user.firstName} placeholder="Happy" />
                    { errors.first_name && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div> }
                  </div>
                </div>
                { errors.first_name && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
              </div>
              <div className="col-span-3 sm:col-span-1">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                  <span>Last Name</span>
                  <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <div className="mt-1 flex rounded-md shadow-sm flex flex-col">
                  <div className='relative'>
                    <input {...register('last_name', { required: true }) } className={errors.last_name ? 'errors' : ''} type="text" placeholder="Hedgehog" defaultValue={user.lastName}/>
                    { errors.last_name && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div> }
                  </div>
                </div>
                { errors.last_name && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
              </div>
              <div className="col-span-3 sm:col-span-1">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                  <span>Email</span>
                  <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <div className="mt-1 flex rounded-md shadow-sm flex flex-col">
                  <div className='relative'>
                    <input {...register('email', { required: true }) } className={errors.email ? 'errors' : ''} type="text" placeholder="Hedgehog" defaultValue={user.email}/>
                    { errors.email && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div> }
                  </div>
                </div>
                { errors.email && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
              </div>
              <div className='col-span-2'>
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Preferred Timezone
                  </label>
                  <select
                    {...register('timezone')}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
                  >
                    { timezones.map((tz) => (
                      <option key={tz.computer} value={tz.computer}>{tz.human.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

          </form>
        </Card>
      </div>
    </div>
  )
}


const SettingsPassword = () => {
  const [user, setUser] = useGlobalState('settingsContainerUser')

  const [, setToast] = useGlobalState('toast')
  const { putpostRequest, getRequest } = useQuery()
  const { formState, reset, register, unregister, handleSubmit, setValue, getValues, setError } = useForm({
   defaultValues: { }
  })

  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)

  const onSubmit = debounce(300, data => {
    setLoading(true)

    putpostRequest(`/api/v1/users/${user.id}/change_password`, 'POST', { user: data }, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        Object.entries(err).forEach(([key, value]) => {
          setError(key, value)
        })
        return
      }

      setToast(randomToastSuccess())
    })
  })


  return (
    <div className='mt-10'>
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Password</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Feeling like your security is a little lax? Change your password here.
            </p>
          </div>
        </div>
        <Card footer={<PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} disabled={!isDirty} text="Save" />}>
          <form>
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1 col-span-3">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                  <span>Old Password</span>
                  <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Minimum 8 characters</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <div className="mt-1 flex rounded-md shadow-sm flex flex-col">
                  <div className='relative'>
                    <input type='password' {...register('old_password', { required: true }) } className={errors.old_password ? 'errors' : ''} />
                    { errors.old_password && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div> }
                  </div>
                </div>
                { errors.old_password && <div className='mt-2 text-sm text-red-600'>This field is required. Minimum 8 characters</div> }
              </div>
              <div className="md:col-span-1 col-span-3">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                  <span>New Password</span>
                  <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Minimum 8 characters</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <div className="mt-1 flex rounded-md shadow-sm flex flex-col">
                  <div className='relative'>
                    <input type='password' {...register('password', { required: true }) } className={errors.password ? 'errors' : ''} />
                    { errors.password && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div> }
                  </div>
                </div>
                { errors.password && <div className='mt-2 text-sm text-red-600'>This field is required. Minimum 8 characters</div> }
              </div>
            </div>

          </form>
        </Card>
      </div>
    </div>
  )
}

const SettingsBilling = () => (
  <div className='mt-10'>
    <div className="md:grid md:grid-cols-3 md:gap-6">
      <div className="md:col-span-1">
        <div className="px-4 sm:px-0">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Billing</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Receipts for everything you've paid us.
          </p>
        </div>
      </div>
      <Card>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Courses, workouts, events and memberships paid through Stripe, with printable receipts.
        </p>
        <Link to="/settings/billing" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-clcpurple hover:opacity-90">
          View billing history
        </Link>
      </Card>
    </div>
  </div>
)

const Settings = ({school}) => {
  return <>
    <SettingsGeneral />
    <SettingsPassword />
    <SettingsBilling />
    <SettingsNotifications />
    <OmniAuthSettings />
    <DeleteAccount />
  </>
}

export default Settings
